import React, { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid"; 

// Gauge Component
const Gauge = ({ score = 0, max = 100, compact = false }) => {
  const pct = max > 0 ? Math.min(Math.max((score / max) * 100, 0), 100) : 0;
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference - (pct / 100) * circumference;

  return (
    <div
      className={`relative ${compact ? "w-28 h-28" : "w-44 h-44"} flex items-center justify-center`}
    >
      <svg viewBox="0 0 120 120" className="w-full h-full" style={{ transform: "rotate(180deg)" }}>
        <circle cx="60" cy="60" r={radius} stroke="#E0E7FF" strokeWidth="10" fill="none" />
        <circle
          cx="60"
          cy="60"
          r={radius}
          stroke="#3B82F6"
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashoffset}
          style={{ transition: "stroke-dashoffset 800ms ease" }}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`${compact ? "text-lg" : "text-2xl"} font-extrabold text-blue-700`}>
          {pct.toFixed(1)}%
        </div>
        <div className={`${compact ? "text-xs" : "text-sm"} text-blue-400`}>
          {score}/{max} pts
        </div>
      </div>
    </div>
  );
};

const GradingScale = ({ rules = [] }) => {
  if (!rules || rules.length === 0) return null;

  const sorted = [...rules].sort((a, b) => a.threshold - b.threshold);

  const rows = [];
  if (sorted.length > 0) {
    rows.push({
      range: `< ${sorted[0].threshold}`,
      grade: sorted[0].label,
    });
  }

  for (let i = 1; i < sorted.length; i++) {
    rows.push({
      range: `${sorted[i - 1].threshold} - ${sorted[i].threshold}`,
      grade: sorted[i].label,
    });
  }

  return (
    <div className="bg-blue-50 rounded-2xl shadow-md p-4">
      <div className="text-sm font-semibold text-blue-700 mb-3">
        Grading Scale
      </div>

      <table className="w-full text-sm text-blue-700 border-collapse">
        <thead>
          <tr className="text-blue-500 border-b">
            <th className="w-1/4 text-left py-1 border-r border-blue-200 pr-3">
              Marks
            </th>
            <th className="text-right py-1 pl-3">
              Grade
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-none">
              <td className="py-1 text-blue-400 border-r border-blue-200 pr-3">
                {r.range}
              </td>
              <td className="py-1 text-right font-medium pl-3">
                {r.grade}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Category Scores Component
const CategoryScores = ({ scores = {} }) => {
  const [collapsed, setCollapsed] = useState(true);

  const entries = Object.entries(scores || {}).filter(
    ([k]) => k && k !== "Uncategorized"
  );
  if (entries.length === 0) return null;

  return (
    <div className="bg-blue-50 p-4 mb-4 rounded-2xl shadow-md">
      {/* Header with toggle */}
      <div
        className="flex justify-between items-center mb-3 cursor-pointer select-none"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="text-lg font-semibold text-blue-800">Category Scores</div>
        <ChevronDownIcon
          className={`h-5 w-5 text-blue-600 transition-transform duration-300 ${
            collapsed ? "rotate-0" : "rotate-180"
          }`}
        />
      </div>

      {/* Collapsible content */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          collapsed ? "max-h-0" : "max-h-[1000px]"
        }`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {entries.map(([cat, val]) => (
            <div
              key={cat}
              className="bg-white rounded-2xl p-4 border shadow hover:shadow-lg transition"
            >
              <div className="font-semibold text-blue-700">{cat}</div>
              <div className="text-sm text-blue-400 mt-1">
                {val?.score ?? 0} / {val?.max ?? 0} pts
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Time Box Component
const TimeBox = ({ timeUsedSec = 0, timeLimitSec = 0 }) => {
  if (!timeUsedSec || !timeLimitSec) return null;

  const pct = Math.min(Math.max((timeUsedSec / timeLimitSec) * 100, 0), 100);

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h > 0 ? String(h).padStart(2, "0") + ":" : ""}${String(m).padStart(2, "0")}:${String(
      sec
    ).padStart(2, "0")}`;
  };

  return (
    <div className="bg-blue-50 rounded-2xl shadow-md p-4 flex flex-col justify-between">
      <div className="text-sm font-semibold text-blue-700 mb-2">Time Used</div>
      <div className="text-lg font-bold text-blue-800 mb-1">{fmt(timeUsedSec)}</div>
      <div className="text-xs text-blue-400 mb-2">/ {fmt(timeLimitSec)}</div>
      <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600"
          style={{ width: `${pct}%`, transition: "width 800ms ease" }}
        />
      </div>
    </div>
  );
};

// Date Box Component
const DateBox = ({ endTime }) => {
  const dateStr = endTime ? new Date(endTime).toLocaleString() : "";
  return (
    <div className="bg-blue-50 rounded-2xl shadow-md p-4 flex flex-col justify-center items-center text-center">
      <div className="text-sm font-semibold text-blue-700 mb-1">Date</div>
      <div className="text-lg font-semibold text-blue-800">{dateStr}</div>
    </div>
  );
};

// ResultCard Component
const ResultCard = ({
  title,
  score = 0,
  max = 0,
  grade = null,
  gradeText = null,
  gradeRules = [],
  scoresByCategory = {},
  modifications = {},
  onRetake,
  timeUsedSec,
  timeLimitSec,
  createdAt,
  author,
  compact = false,
}) => {
  const hideMarks = modifications.hideMarks === true;

  const showGrading = !hideMarks && gradeRules?.length > 0;
  const showTimer = !hideMarks && timeUsedSec && timeLimitSec;

  console.log(gradeText);

  return (
    <div className="flex justify-center w-full">
      <div
  className={`bg-white/95 shadow-xl rounded-3xl p-6 flex flex-col w-full border border-blue-100 backdrop-blur-xl ${
    compact ? "max-w-sm md:max-w-md" : "max-w-xl md:max-w-4xl"
  }`}
>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-6 md:gap-6 gap-4">
          <div className="flex flex-col gap-1">
            <div className="text-xl md:text-2xl font-bold text-blue-800">{title || "Test Result"}</div>
            {author && <div className="text-sm text-blue-600 italic">{`By ${author}`}</div>}
          </div>

           {/* Gauge container */}
            <div className="flex justify-center">
              {hideMarks ? (
                <>
                  {/* Mobile: nothing */}
                  <div className="block md:hidden" />
                  {/* Desktop: invisible but keeps width */}
                  <div className="hidden md:block md:invisible">
                    <Gauge score={score} max={max} compact={false} />
                  </div>
                </>
              ) : (
                <Gauge score={score} max={max} compact={false} />
              )}
            </div>
  
        </div>

        {/* Grade Section */}
      {!compact && (grade || gradeText?.trim()) && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-2xl shadow-md text-center my-4 w-full max-w-sm mx-auto">
          
          {/* Grade */}
          {grade && (
            <div className="text-xl md:text-2xl font-bold text-blue-800">
              Grade: {grade}
            </div>
          )}

          {/* Grade Text */}
          {gradeText?.trim() && (
            <div className="text-sm md:text-base text-blue-500 mt-1">
              {gradeText}
            </div>
          )}

        </div>
      )}

    {/* Info Grid */}
    <div
      className={`grid gap-4 mb-6 ${
        compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-[65%_35%]"
      }`}
    >
      {/* Left (65%) - Grading Scale */}
      {showGrading && (
        <div className="h-full">
          <GradingScale rules={gradeRules} />
        </div>
      )}

      {/* Right (35%) - Time + Date */}
      <div className="flex flex-col gap-4">
        {showTimer && (
          <TimeBox
            timeUsedSec={timeUsedSec}
            timeLimitSec={timeLimitSec}
          />
        )}

        <DateBox endTime={createdAt || new Date()} />
      </div>
    </div>

        {/* Category Scores */}
        {!hideMarks && Object.keys(scoresByCategory || {}).length > 0 && (
          <CategoryScores scores={scoresByCategory} />
        )}

  


        {/* Retake Button */}
        <div className="mt-auto flex justify-center">
          <button
            onClick={onRetake}
            className="px-6 py-2 md:px-8 md:py-3 font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-2xl shadow-md transition"
          >
            Retake Test
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultCard;